import {ComponentProps, For, JSX, splitProps, untrack} from 'solid-js';
import {LangPackKey, i18n} from '@lib/langPack';
import {AvatarNew} from '@components/avatarNew';
import {PeerTitleTsx} from '@components/peerTitleTsx';
import classNames from '@helpers/string/classNames';

import styles from '@components/table.module.scss';
import {NULL_PEER_ID} from '@appManagers/constants';
import Button from '@components/buttonTsx';
import showTooltip from '@components/tooltip';

export type TableRow = [LangPackKey | JSX.Element, JSX.Element];

const keyCellClass = classNames(styles.cell, styles.key);

export default function Table(props: {
  class?: string,
  content: TableRow[]
  boldKey?: boolean
  footer?: JSX.Element
  footerClass?: string
  cellClass?: string
  keyCellClass?: string
}) {
  return (
    <table
      class={classNames(
        styles.table,
        props.boldKey && styles.boldKey,
        props.class
      )}
    >
      <For each={props.content}>
        {([key, value]) => (
          <tr>
            <td class={classNames(keyCellClass, props.cellClass, props.keyCellClass)}>
              {typeof key === 'string' ? i18n(key as LangPackKey) : key}
            </td>
            <td class={classNames(styles.cell, props.cellClass)}>
              <div class={/* @once */ styles.value}>
                {value}
              </div>
            </td>
          </tr>
        )}
      </For>
      {props.footer && (
        <tr>
          <td class={props.footerClass ?? classNames(styles.cell, props.cellClass)} colspan={2}>
            {props.footer}
          </td>
        </tr>
      )}
    </table>
  );
}

export function TablePeer(props: {
  peerId: PeerId,
  onClick?: () => void
}) {
  const avatar = untrack(() => AvatarNew({peerId: props.peerId, size: 24}));
  return (
    <div
      class={/* @once */ styles.peer}
      onClick={props.onClick}
    >
      {props.peerId === NULL_PEER_ID ? (
        <>
          <div class="popup-star-gift-info-anonymous">
            <img src="assets/img/anon_paid_reaction.png" alt="Anonymous" />
          </div>
          {i18n('StarGiftHiddenUser')}
        </>
      ) : (
        <>
          {avatar.element}
          <PeerTitleTsx peerId={props.peerId} />
        </>
      )}
    </div>
  );
}

export function TableButton(props: ComponentProps<typeof Button>) {
  return (
    <Button
      {...props}
      class={/* @once */ styles.button}
    />
  )
}

export function TableButtonWithTooltip(props: ComponentProps<typeof Button> & {
  tooltipClass?: string
  tooltipTextElement?: HTMLElement
}) {
  const [, rest] = splitProps(props, ['tooltipClass', 'tooltipTextElement']);
  return (
    <Button
      {...rest}
      class={classNames(rest.class, styles.button)}
      onClick={(evt) => {
        props.onClick?.(evt);
        if(props.tooltipTextElement) showTooltip({
          element: evt.target as HTMLElement,
          vertical: 'top',
          container: document.body,
          class: props.tooltipClass,
          textElement: props.tooltipTextElement
        });
      }}
    />
  )
}
